import axios from 'axios';
import type { 
  ConversationRequest,
  AnswerRequest,
  FinalizeRequest,
  SubjectListResponse,
  ConversationResponse
} from '@/types/chat';
import { API_BASE_URL } from '@/lib/config';
import { getSebKeys } from '../utils/seb';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, remove it and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

class ChatService {
  // Get service status
  async getStatus(): Promise<string> {
    const response = await apiClient.get<string>('/chat/status');
    return response.data;
  }

  // Get list of subjects for a specific module (optional: specific class assignment)
  async getSubjectList(moduleId: string, classId?: string): Promise<SubjectListResponse[]> {
    try {
      const params = new URLSearchParams({ moduleId });
      if (classId) {
        params.set('classId', classId);
      }
      const response = await apiClient.get<SubjectListResponse[]>(`/chat/SubjectList?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Initialize a new conversation
  async initializeConversation(subjectId: string, classId?: string): Promise<ConversationResponse> {
    const request: ConversationRequest = {
      id: subjectId,
      classId,
    };

    // Läuft die App in Safe Exam Browser, liefert die SEB-API den Config Key inkl. der
    // Seiten-URL, mit der er verknüpft ist. Beides wird als Header mitgeschickt, damit das
    // Backend den Hash serverseitig nachrechnen und den SEB-Zugriff verifizieren kann (siehe
    // src/utils/seb.ts für den Hintergrund, warum der native SEB-HTTP-Header hier nicht
    // ankommt).
    const sebKeys = await getSebKeys();
    const headers = sebKeys
      ? {
          'X-SafeExamBrowser-ConfigKeyHash': sebKeys.configKey,
          'X-Seb-Page-Url': sebKeys.pageUrl,
        }
      : undefined;

    const response = await apiClient.post<ConversationResponse>('/chat/initialize', request, { headers });
    return response.data;
  }


  // Submit an answer to the current question
  async submitAnswer(conversationId: string, answer: string): Promise<ConversationResponse> {
    const request: AnswerRequest = {
      id: conversationId,
      text: answer,
    };
    
    const response = await apiClient.post<ConversationResponse>('/chat/answer', request);
    return response.data;
  }

  // Cancel conversation and calculate grade
  async cancelConversation(conversationId: string): Promise<string> {
    const request: FinalizeRequest = {
      id: conversationId,
    };
    
    const response = await apiClient.post<string>('/chat/cancel', request);
    return response.data;
  }

  // Cancel all open conversations for current user
  async cancelAllConversations(): Promise<string> {
    const response = await apiClient.post<string>('/chat/cancelall');
    return response.data;
  }

  // Get conversation by ID
  async getConversation(conversationId: string): Promise<ConversationResponse> {
    const response = await apiClient.get<ConversationResponse>(`/chat/${conversationId}`);
    return response.data;
  }
}

export const chatService = new ChatService();

export default chatService;
